import type {
  AdditionalDocumentEntry,
  AdditionalDocumentScanStatus,
  AddressDetails,
  AddressSuggestion,
  AuditLogEntry,
  EmailVerifyResult,
  FileEntry,
  FileEntryCondition,
  FormColumn,
  FormRow,
  FormSection,
  OneSpanConfigPayload,
  ReorderDirection,
  StepFormBranch,
  ThankYouConfig,
  WelcomeConfig,
  WorkflowField,
  WorkflowFieldPropertiesPayload,
  WorkflowFieldType,
  WorkflowFormData,
  WorkflowStep,
  WorkflowStepType,
  WorkflowSummary,
  WorkflowTemplate,
  WorkflowVersion,
  WorkflowVersionSummary,
} from '@bmo/form-core/template';

import { extractApiError } from '@/originator/statusUtils';

import { apiClient, FILE_TRANSFER_TIMEOUT_MS } from './client';

/**
 * The workflow domain types moved to `@bmo/form-core` so `PdfFunction` can fill
 * PDFs against the same graph server-side (ADR-0031). They are re-exported here
 * unchanged, so every existing `from '@/api/workflows'` import keeps resolving and
 * this file is once again just the HTTP client for those types.
 */
export * from '@bmo/form-core/template';

/** Body of PUT /sections/{id}/repeatable. Whole-state: an omitted bound, label or
 *  `repeatWithSectionId` is stored as absent. */
export type SectionRepeatablePayload = {
  repeatable: boolean;
  minInstances?: number;
  maxInstances?: number | null;
  instanceLabelEn?: string | null;
  instanceLabelFr?: string | null;
  /** "Repeat once per instance of" — the repeatable section this one mirrors. */
  repeatWithSectionId?: string | null;
  /** This section's instance count feeds `appInfo.jointApplication` — a count
   *  ABOVE `jointApplicationThreshold` reports the application as joint.
   *  Requires `repeatable`. */
  jointApplicationSelector?: boolean;
  /** The instance count the tag must EXCEED to read as joint. 1 (the default, and
   *  the reading every section had before this was authorable) = a per-party
   *  section that already holds the primary party. 0 = an optional block holding
   *  only the extra party, joint as soon as it holds anything. */
  jointApplicationThreshold?: number;
};

export const workflowsApi = {
  /** Dashboard list. Returns metadata only — see {@link WorkflowSummary} for why
   *  `steps` is absent and what to call when you need it. */
  list: async (): Promise<WorkflowSummary[]> => {
    const res = await apiClient.get<WorkflowSummary[]>('/workflows');
    return res.data;
  },

  /**
   * Single indexed lookup by appName — used by the advisor-entry route, which arrives with
   * the appName on the URL and needs exactly one workflow, not the whole team's list. Null
   * on 404 (unknown appName, or gated out for this advisor's dealer code) rather than
   * throwing, so callers can treat "not found" and "not eligible" the same way `list()` +
   * a client-side `.find()` used to.
   */
  getByAppName: async (appName: string): Promise<WorkflowSummary | null> => {
    try {
      const res = await apiClient.get<WorkflowSummary>(
        `/workflows/by-app-name/${encodeURIComponent(appName)}`,
      );
      return res.data;
    } catch (err) {
      if (extractApiError(err).status === 404) return null;
      throw err;
    }
  },

  /** `versionLabel` (advisor reads only) asks for a specific APPROVED version's form — how a
   *  submitted application is shown on the version it was filed on (SFA-8). */
  get: async (id: string, opts?: { versionLabel?: string }): Promise<WorkflowTemplate> => {
    const res = await apiClient.get<WorkflowTemplate>(`/workflows/${id}`, {
      params: opts?.versionLabel ? { versionLabel: opts.versionLabel } : undefined,
    });
    return res.data;
  },

  create: async (data: WorkflowFormData): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>('/workflows', data);
    return res.data;
  },

  update: async (
    id: string,
    data: Omit<WorkflowFormData, 'appName'>,
  ): Promise<WorkflowTemplate> => {
    const res = await apiClient.patch<WorkflowTemplate>(`/workflows/${id}`, data);
    return res.data;
  },

  /**
   * IMPORTANT: only fire this when the form state has actually changed since the last
   * persisted snapshot. The BE refreshes the workflow's editor lock on every save call,
   * so a timer-driven autosave (firing on an interval regardless of changes) would keep
   * an idle tab's lock alive forever and break the AC09 14h expiry rule. Dirty-flag gate
   * any caller before invoking.
   */
  autosave: async (
    id: string,
    data: Omit<WorkflowFormData, 'appName'>,
  ): Promise<WorkflowTemplate> => {
    const res = await apiClient.put<WorkflowTemplate>(`/workflows/${id}/autosave`, data);
    return res.data;
  },

  submit: async (id: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/submit`);
    return res.data;
  },

  clone: async (sourceId: string, newAppName: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${sourceId}/clone`, {
      newAppName,
    });
    return res.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/workflows/${id}`);
  },

  /** Archived workflows, newest-archived first. Readable by both roles. */
  listArchived: async (): Promise<WorkflowTemplate[]> => {
    const res = await apiClient.get<WorkflowTemplate[]>('/workflows/archived');
    return res.data;
  },

  /**
   * Retire a workflow: Approved, Rejected, or anything already published
   * (`currentVersionNumber > 0`) whatever the status of an in-flight new
   * version. Pending is refused. Approver-only on the BE.
   */
  archive: async (id: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/archive`);
    return res.data;
  },

  /** Restore an archived workflow. It comes back as a DRAFT, not to the
   *  status it held when archived. Approver-only on the BE. */
  unarchive: async (id: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/unarchive`);
    return res.data;
  },

  setActive: async (
    id: string,
    isActive: boolean,
    language?: string,
  ): Promise<WorkflowTemplate> => {
    // ?lang=<en|fr> drives the "your workflow is now live" SES email body
    // when the flip goes from inactive → active (AC6 on the Publish story).
    // Optional so unit tests / non-i18n contexts don't break.
    const qs = language ? `?lang=${encodeURIComponent(language)}` : '';
    const res = await apiClient.patch<WorkflowTemplate>(`/workflows/${id}/active${qs}`, {
      isActive,
    });
    return res.data;
  },

  /**
   * Persist a new display order for one or more Approved workflows. Used by the
   * Originator's drag-to-reorder on the Published tab — the FE sends every
   * visible Published card with its new 0..N position so the BE writes a
   * coherent run instead of stitching partial updates.
   */
  reorderPublished: async (items: ReadonlyArray<{ id: string; order: number }>): Promise<void> => {
    await apiClient.post('/workflows/reorder', { items });
  },

  /** Version history WITHOUT step snapshots — see {@link WorkflowVersionSummary}. */
  listVersions: async (id: string): Promise<WorkflowVersionSummary[]> => {
    const res = await apiClient.get<WorkflowVersionSummary[]>(`/workflows/${id}/versions`);
    return res.data;
  },

  /** One version WITH its frozen step snapshot. Use when rendering a version's content
   *  (preview, step config, approver diff) rather than just its row in the history. */
  getVersion: async (id: string, versionNumber: number): Promise<WorkflowVersion> => {
    const res = await apiClient.get<WorkflowVersion>(`/workflows/${id}/versions/${versionNumber}`);
    return res.data;
  },

  revert: async (id: string, versionNumber: number): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(
      `/workflows/${id}/versions/${versionNumber}/revert`,
    );
    return res.data;
  },

  activateVersion: async (id: string, versionNumber: number): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(
      `/workflows/${id}/versions/${versionNumber}/activate`,
    );
    return res.data;
  },

  /** Approver-only: promote the staged pending-approved version to live.
   *  Demotes the prior current version + clears the template's pending
   *  fields. Fires when the Approver toggles publish on the "Pending
   *  Publishing" card after a re-approval. `language` drives the
   *  "your workflow is now live" email body (AC6 on the Publish story). */
  promotePendingApproval: async (id: string, language?: string): Promise<WorkflowTemplate> => {
    const qs = language ? `?lang=${encodeURIComponent(language)}` : '';
    const res = await apiClient.post<WorkflowTemplate>(
      `/workflows/${id}/promote-pending-approval${qs}`,
    );
    return res.data;
  },

  startNewVersion: async (id: string, sourceVersionNumber?: number): Promise<WorkflowTemplate> => {
    // Always send a JSON body — the BE endpoint declares a StartNewVersionRequest? param,
    // and minimal API returns 415 when there's no Content-Type. The default-fork path sends
    // an explicit null so the shape is consistent either way.
    const body = { sourceVersionNumber: sourceVersionNumber ?? null };
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/start-new-version`, body);
    return res.data;
  },

  cancelNewVersion: async (id: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/cancel-new-version`);
    return res.data;
  },

  /**
   * Withdraw a Pending submission before the approver decides. Originator-only,
   * owner-only. v1-pending → template returns to Draft; v2+ pending → template
   * returns to Approved with v1 left serving live. `language` drives the
   * cancellation email's single-language render (same shape as approve/reject).
   */
  cancelPending: async (id: string, language?: string): Promise<WorkflowTemplate> => {
    const qs = language ? `?lang=${encodeURIComponent(language)}` : '';
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/cancel-pending${qs}`);
    return res.data;
  },

  // ----- Steps -----

  addStep: async (
    templateId: string,
    body: {
      type: WorkflowStepType;
      titleEn: string;
      titleFr?: string;
      /** Rail / Application Review label; blank or omitted falls back to the title. A step is
       *  normally created without one and given it on a later edit. */
      navTitleEn?: string;
      navTitleFr?: string;
      descriptionEn?: string;
      descriptionFr?: string;
    },
  ): Promise<WorkflowStep> => {
    const res = await apiClient.post<WorkflowStep>(`/workflows/${templateId}/steps`, body);
    return res.data;
  },

  editStep: async (
    templateId: string,
    stepId: string,
    body: {
      titleEn: string;
      titleFr?: string;
      descriptionEn?: string;
      descriptionFr?: string;
      /** Withhold the step from the public customer runtime. Omitted = leave the current
       *  value alone, which is what the Custom Editor's rename-only callers want. The BE
       *  refuses `true` on any step type but Dynamic Form. */
      advisorOnly?: boolean;
      /** Rail / Application Review label.
       *
       *  OMIT to leave the stored value alone; send `''` to CLEAR it back to "use the title".
       *  The two are not interchangeable, and the difference has a caller: `WelcomeConfigModal`
       *  PATCHes this route with title + description only, to keep the rail label in step with
       *  the welcome card, and must not wipe an authored nav title on the way past. */
      navTitleEn?: string;
      navTitleFr?: string;
    },
  ): Promise<WorkflowStep> => {
    const res = await apiClient.patch<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}`,
      body,
    );
    return res.data;
  },

  deleteStep: async (templateId: string, stepId: string): Promise<void> => {
    await apiClient.delete(`/workflows/${templateId}/steps/${stepId}`);
  },

  reorderStep: async (
    templateId: string,
    stepId: string,
    direction: ReorderDirection,
  ): Promise<void> => {
    await apiClient.post(`/workflows/${templateId}/steps/${stepId}/reorder`, { direction });
  },

  // ----- PDF entries (PdfFileUpload step) -----

  addFileEntry: async (
    templateId: string,
    stepId: string,
    body: { label: string; enFileKey?: string | null; frFileKey?: string | null },
  ): Promise<FileEntry> => {
    const res = await apiClient.post<FileEntry>(
      `/workflows/${templateId}/steps/${stepId}/file-entries`,
      body,
    );
    return res.data;
  },

  editFileEntry: async (
    templateId: string,
    stepId: string,
    entryId: string,
    /** Whole-state: an omitted `labelFr` / `filenameTemplate` / `filenameTemplateFr` /
     *  `instanceScope` is CLEARED on the server. */
    body: {
      label: string;
      labelFr?: string | null;
      enFileKey?: string | null;
      frFileKey?: string | null;
      filenameTemplate?: string | null;
      filenameTemplateFr?: string | null;
      instanceScope?: string | null;
    },
  ): Promise<FileEntry> => {
    const res = await apiClient.patch<FileEntry>(
      `/workflows/${templateId}/steps/${stepId}/file-entries/${entryId}`,
      body,
    );
    return res.data;
  },

  deleteFileEntry: async (templateId: string, stepId: string, entryId: string): Promise<void> => {
    await apiClient.delete(`/workflows/${templateId}/steps/${stepId}/file-entries/${entryId}`);
  },

  /**
   * Replace the gating conditions on a PDF entry. Pass an empty list with a
   * null combinator to clear ("always include"); pass a non-empty list with
   * "all" or "any" to apply gating. The BE persists the list as-is and
   * evaluates it at PDF-generation time.
   */
  setFileEntryConditions: async (
    templateId: string,
    stepId: string,
    entryId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<FileEntry> => {
    const res = await apiClient.put<FileEntry>(
      `/workflows/${templateId}/steps/${stepId}/file-entries/${entryId}/conditions`,
      body,
    );
    return res.data;
  },

  /** Replace the field's visibility conditions. Same wire shape as the PDF
   *  endpoint. Empty list + null combinator clears the gating. */
  setFieldConditions: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.put<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/conditions`,
      body,
    );
    return res.data;
  },

  /** Replace the section's visibility conditions. */
  setSectionConditions: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<FormSection> => {
    const res = await apiClient.put<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/conditions`,
      body,
    );
    return res.data;
  },

  /** Replace the step's visibility conditions. Source fields must live in
   *  PRIOR steps — scope enforced by the picker; the BE refuses self-reference. */
  setStepConditions: async (
    templateId: string,
    stepId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/conditions`,
      body,
    );
    return res.data;
  },

  /** Replace the step's form-branch rules — "answer this way and the rest of the
   *  workflow becomes a different form". Empty list clears them. BE refuses them
   *  on a non-DynamicForm step. */
  setStepFormBranches: async (
    templateId: string,
    stepId: string,
    body: { formBranches: StepFormBranch[] },
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/form-branches`,
      body,
    );
    return res.data;
  },

  // ----- "Disable if" rules -----
  // Same wire shape and the same clearing semantics as the three visibility endpoints
  // above; only the runtime effect differs. When the rule MATCHES the target renders
  // read-only but stays visible, stays navigable, and its answers still ship.

  /** Replace the step's "Disable if" rule. BE refuses it on a non-DynamicForm step. */
  setStepDisableConditions: async (
    templateId: string,
    stepId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/disable-conditions`,
      body,
    );
    return res.data;
  },

  /** Replace the section's "Disable if" rule. */
  setSectionDisableConditions: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<FormSection> => {
    const res = await apiClient.put<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/disable-conditions`,
      body,
    );
    return res.data;
  },

  /** Replace the field's "Disable if" rule. */
  setFieldDisableConditions: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.put<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/disable-conditions`,
      body,
    );
    return res.data;
  },

  // ----- DynamicForm builder canvas (sections → rows → columns → fields) -----

  addFormSection: async (
    templateId: string,
    stepId: string,
    payload: { titleEn: string; titleFr?: string | null },
  ): Promise<FormSection> => {
    const res = await apiClient.post<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections`,
      payload,
    );
    return res.data;
  },

  editFormSection: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    // `hideEditInReview` omitted = unchanged on the server, which is what keeps the canvas's
    // title-only inline rename from resetting it.
    payload: { titleEn: string; titleFr?: string | null; hideEditInReview?: boolean },
  ): Promise<FormSection> => {
    const res = await apiClient.patch<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}`,
      payload,
    );
    return res.data;
  },

  deleteFormSection: async (
    templateId: string,
    stepId: string,
    sectionId: string,
  ): Promise<void> => {
    await apiClient.delete(`/workflows/${templateId}/steps/${stepId}/sections/${sectionId}`);
  },

  setSectionRepeatable: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    payload: SectionRepeatablePayload,
  ): Promise<FormSection> => {
    const res = await apiClient.put<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/repeatable`,
      payload,
    );
    return res.data;
  },

  reorderFormSection: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    direction: 'Up' | 'Down',
  ): Promise<void> => {
    await apiClient.post(`/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/reorder`, {
      direction,
    });
  },

  /** Places a fresh, independent copy of a saved Section custom element into this step (appended at the end). */
  placeCustomSection: async (
    templateId: string,
    stepId: string,
    customElementId: string,
  ): Promise<FormSection> => {
    const res = await apiClient.post<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections/place-custom/${customElementId}`,
    );
    return res.data;
  },

  /** Creates a new DynamicForm step pre-populated from a saved Step custom element (fresh ids, appended at the end). */
  placeCustomStep: async (templateId: string, customElementId: string): Promise<WorkflowStep> => {
    const res = await apiClient.post<WorkflowStep>(
      `/workflows/${templateId}/steps/place-custom-step/${customElementId}`,
    );
    return res.data;
  },

  addFormRow: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    columnCount: number,
  ): Promise<FormRow> => {
    const res = await apiClient.post<FormRow>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows`,
      { columnCount },
    );
    return res.data;
  },

  deleteFormRow: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}`,
    );
  },

  reorderFormRow: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    direction: 'Up' | 'Down',
  ): Promise<void> => {
    await apiClient.post(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/reorder`,
      { direction },
    );
  },

  /**
   * Drops a row at an arbitrary position in its section. Used to insert a new
   * row mid-list: the POST /rows endpoint always appends, so the canvas creates
   * the row then slides it into place. Out-of-range targets clamp server-side.
   */
  moveFormRow: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    targetIndex: number,
  ): Promise<void> => {
    await apiClient.post(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/move`,
      { targetIndex },
    );
  },

  addFormColumn: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
  ): Promise<FormColumn> => {
    const res = await apiClient.post<FormColumn>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns`,
    );
    return res.data;
  },

  removeFormColumn: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}`,
    );
  },

  placeField: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    payload: {
      type: WorkflowFieldType;
      labelEn: string;
      labelFr?: string | null;
      required?: boolean;
      properties?: WorkflowFieldPropertiesPayload;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.post<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field`,
      payload,
    );
    return res.data;
  },

  editField: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    payload: {
      labelEn: string;
      labelFr?: string | null;
      required?: boolean;
      properties?: WorkflowFieldPropertiesPayload;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field`,
      payload,
    );
    return res.data;
  },

  // Set (or clear) a single repeatable column's PDF AcroForm mapping handle —
  // used by the visual PDF-mapping page. Repeatable columns keep the scalar 1:1
  // mapping; scalar fields use togglePdfMapping instead. Unlike editField (a
  // wholesale property replace), this leaves the rest of the field untouched.
  // Null/blank internalName clears the column's mapping.
  setFieldInternalName: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      internalName: string | null;
      repeatableColumnId: string;
      rowIndex: number;
      optionValue?: string | null;
      segment?: string | null;
      // WHICH document this handle fills. Part of the mapping's identity, so a
      // clear reaches only this entry's handle — omitting it targets the legacy
      // "every document" slot rather than acting on all of them.
      pdfEntryId?: string | null;
      // Where the value comes from: null/absent is the CELL at rowIndex, which
      // is what every handle authored before this was. "total" is the column's
      // cross-row sum, which belongs to no cell.
      //
      // Part of the mapping's identity too, and for the sharpest version of the
      // same reason as pdfEntryId: a total is stored under rowIndex 1, so a call
      // that omits it addresses row 1's own cell handle instead — authoring the
      // total would clear the cell, and clearing the total would match nothing.
      source?: 'total' | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/internal-name`,
      body,
    );
    return res.data;
  },

  // Toggle one field-level PDF AcroForm mapping (document entry + AcroForm field
  // name) on a scalar field — used by the visual PDF-mapping page. Adds the pair
  // when absent, removes it when present (click a box to map, click again to
  // unmap). Leaves the field's other properties untouched.
  togglePdfMapping: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: { pdfEntryId: string; acroFieldName: string; instanceIndex?: number | null },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/pdf-mapping`,
      body,
    );
    return res.data;
  },

  // Toggle one AcroForm box that prints a repeatable SECTION's instance count —
  // "2" in the "number of accountholders" slot. The section twin of
  // togglePdfMapping: adds the handle when absent, removes it when present.
  //
  // Keyed on the section rather than on a field because that is what it counts.
  // Nothing stores the number — it is derived from the instance keys at fill
  // time — so this authors only WHERE it prints. Returns the updated section.
  toggleSectionCountPdfMapping: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    body: { pdfEntryId: string; acroFieldName: string; language?: string | null },
  ): Promise<FormSection> => {
    const res = await apiClient.patch<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/count-pdf-mapping`,
      body,
    );
    return res.data;
  },

  // Set (or clear) which PART of a field's value one existing PDF mapping
  // writes — `year` / `month` / `day`, or a 1-based character range like `4-6`.
  // Null/empty restores "write the whole value". Separate from
  // togglePdfMapping because the segment belongs to a mapping that already
  // exists: the originator maps a box first, then says which piece goes in it.
  setPdfMappingSegment: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      pdfEntryId: string;
      acroFieldName: string;
      segment?: string | null;
      instanceIndex?: number | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/pdf-mapping-segment`,
      body,
    );
    return res.data;
  },

  // Set (or clear) WHICH RENDERING of a field's answer one existing mapping
  // writes: `label` puts the chosen option's label in the box instead of its
  // stored value (the province picker stores `YT`, the paper form's line reads
  // "Yukon"), `formatted` runs a number through the field's own numberFormat (a
  // subscription amount printed `250000` on a document the client signs). Null
  // restores the stored value. Field-level only — a per-option handle ticks a
  // checkbox, so there is no rendering to pick.
  setPdfMappingValueSource: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      pdfEntryId: string;
      acroFieldName: string;
      valueSource?: string | null;
      instanceIndex?: number | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/pdf-mapping-value-source`,
      body,
    );
    return res.data;
  },

  // Gate one existing FIELD-level mapping to a single option: it writes only
  // while that option is the answer. Null/blank clears the gate, restoring
  // "fires on whatever is stored" — what every mapping did before the gate
  // existed.
  //
  // For a question whose answer is also what gets PRINTED. The sub-agreements'
  // "Is this a bulk request?" prints a sentence into the subscriber boxes on
  // Yes; ungated, answering No puts the literal "no" in the same boxes.
  //
  // Field-level only: a per-option handle is already scoped to its own option,
  // so `optionValue` here is the gate being SET, not an address.
  setPdfMappingOptionValue: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      pdfEntryId: string;
      acroFieldName: string;
      optionValue?: string | null;
      instanceIndex?: number | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/pdf-mapping-option-value`,
      body,
    );
    return res.data;
  },

  // Hold one existing field-level mapping OUT of the fill-time waterfall, or put
  // it back. An addendum is applied after the whole walk and appends to whatever
  // won the box, so — unlike a plain joinWith, which still writes at its own
  // point in the walk — nothing later in the form can wipe it. For the paper form
  // that gives ONE line to two answers ("<dealer name> <dealer code>").
  setPdfMappingAddendum: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      pdfEntryId: string;
      acroFieldName: string;
      isAddendum: boolean;
      instanceIndex?: number | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/pdf-mapping-addendum`,
      body,
    );
    return res.data;
  },

  // Set (or clear) the separator an addendum appends with — the scalar twin of
  // setRepeatableColumnJoinWith below. Only meaningful alongside isAddendum: on
  // an unflagged mapping joinWith still appends, but at the mapping's own point
  // in the walk, which is the behaviour the addendum flag exists to replace.
  setPdfMappingJoinWith: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      pdfEntryId: string;
      acroFieldName: string;
      joinWith?: string | null;
      instanceIndex?: number | null;
      optionValue?: string | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/pdf-mapping-join-with`,
      body,
    );
    return res.data;
  },

  // Restrict one existing PDF mapping to a single language of its entry's
  // document, or clear the restriction. For templates whose AcroForm names
  // agree but whose meanings do not (RIF's swapped language checkbox).
  // `optionValue` targets a per-option handle; omit it for the field's own.
  setPdfMappingLanguage: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      pdfEntryId: string;
      acroFieldName: string;
      language?: 'en' | 'fr' | null;
      instanceIndex?: number | null;
      optionValue?: string | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/pdf-mapping-language`,
      body,
    );
    return res.data;
  },

  // Set (or clear) the separator with which one repeatable-column handle APPENDS
  // to its PDF box instead of replacing it — how two columns share one box
  // ("BMO Money Market Fund / 1234"). The mapping is identified exactly as the
  // handle toggle keys it and must already exist, so this can never create or
  // remove one. Null/empty restores plain replace.
  setRepeatableColumnJoinWith: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      repeatableColumnId: string;
      rowIndex: number;
      internalName: string;
      optionValue?: string | null;
      segment?: string | null;
      joinWith?: string | null;
      // Identifies which document's handle to edit — keyed exactly as the
      // toggle above keys it.
      pdfEntryId?: string | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/repeatable-column-join`,
      body,
    );
    return res.data;
  },

  // Toggle one per-option PDF AcroForm checkbox mapping on a choice field, keyed
  // by the option value — used by the visual PDF-mapping page. Adds the pair
  // when absent, removes it when present.
  toggleOptionPdfMapping: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      optionValue: string;
      pdfEntryId: string;
      acroFieldName: string;
      instanceIndex?: number | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/option-pdf-mapping`,
      body,
    );
    return res.data;
  },

  removeField: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field`,
    );
  },

  moveField: async (
    templateId: string,
    stepId: string,
    payload: {
      fromSectionId: string;
      fromRowId: string;
      fromColumnId: string;
      toSectionId: string;
      toRowId: string;
      toColumnId: string;
    },
  ): Promise<void> => {
    await apiClient.post(`/workflows/${templateId}/steps/${stepId}/move-field`, payload);
  },

  /**
   * Single-shot insert of a multi-field compound (Address etc.) into a section.
   * Replaces what was previously a loop of N addFormRow + N×M placeField calls
   * — collapses to one BE round trip and one MutateUnderLockAsync cycle.
   */
  /**
   * Upload one help-bubble image (used inside a markdown body). Streams the file
   * through the API; the server stores it under `workflows/{id}/help-images/...`
   * and returns a stable, non-expiring URL the originator pastes into the body.
   */
  uploadHelpImage: async (
    templateId: string,
    file: File,
  ): Promise<{ id: string; filename: string; url: string; contentType: string }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post<{
      id: string;
      filename: string;
      url: string;
      contentType: string;
    }>(`/workflows/${templateId}/help-images`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: FILE_TRANSFER_TIMEOUT_MS,
    });
    return res.data;
  },

  /**
   * Canada Post AddressComplete search. Returns a list of suggestions; the
   * caller drills via `lastId` when picking a row whose `next === 'Find'`,
   * or fires `addressLookupRetrieve` when picking a row with `next ===
   * 'Retrieve'`.
   */
  addressLookupFind: async (
    query: string,
    country: string = 'CAN',
    lastId?: string | null,
  ): Promise<AddressSuggestion[]> => {
    const params = new URLSearchParams({ q: query, country });
    if (lastId) params.set('lastId', lastId);
    const res = await apiClient.get<AddressSuggestion[]>(
      `/integrations/address/find?${params.toString()}`,
    );
    return res.data ?? [];
  },

  /**
   * Resolve a leaf suggestion id (from {@link addressLookupFind}) into a
   * structured address. The FE consumer maps fields onto sibling form
   * inputs (Address line / City / Province / Postal Code).
   */
  addressLookupRetrieve: async (id: string): Promise<AddressDetails | null> => {
    try {
      const res = await apiClient.get<AddressDetails>(
        `/integrations/address/retrieve?id=${encodeURIComponent(id)}`,
      );
      return res.data ?? null;
    } catch {
      return null;
    }
  },

  /**
   * ZeroBounce batch email verification (INFLOWS-188). Fires when the
   * advisor lands on the OneSpan E-signature step — every signatory email
   * on that step goes up in one call. Returns results in the same order
   * as the request. Any `Invalid` result blocks Next in the runtime.
   *
   * On any transport/HTTP failure, returns Unknown for every input. That is
   * NO LONGER a soft-degrade for an advisor: since 2026-10-06 `Unknown` blocks
   * Next in the advisor runtime (BMO's call — hard block, no override), so an
   * outage, an expired ZeroBounce key, a 429 from our own rate limiter or a
   * 4xx from a bad auth policy all stop the advisor on this step rather than
   * waving an unverified address through to OneSpan. Preview is unchanged.
   * The gate itself is `eSignEmailGate`.
   *
   * The console.error breadcrumb matters more because of that, not less — it
   * is the only trail distinguishing "ZeroBounce said it couldn't tell" from
   * "we never reached ZeroBounce", and the second one is an incident.
   */
  verifyEmails: async (emails: string[]): Promise<EmailVerifyResult[]> => {
    if (emails.length === 0) return [];
    try {
      const res = await apiClient.post<{ results: EmailVerifyResult[] }>(
        '/integrations/email/verify',
        { emails },
      );
      return res.data?.results ?? [];
    } catch (err) {
      console.error('verifyEmails failed — degrading to Unknown for every input.', err);
      // Graceful-degrade: FE sees Unknown → Next stays enabled. Matches
      // the BE contract when the ZeroBounce client itself soft-disables.
      return emails.map((email) => ({
        email,
        status: 'Unknown',
        subStatus: 'http_error',
        didYouMean: null,
      }));
    }
  },

  insertCompound: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    payload: {
      rows: {
        // `null` in a column slot means "leave this grid cell empty" — the
        // domain reserves the width but doesn't place a field. Lets a single
        // narrow input sit on a wider row (e.g. Postal Code on a 3-col grid).
        columns: ({
          type: WorkflowFieldType;
          labelEn: string;
          labelFr?: string | null;
          required?: boolean;
          properties?: WorkflowFieldPropertiesPayload;
        } | null)[];
      }[];
    },
  ): Promise<{ rows: FormRow[] }> => {
    const res = await apiClient.post<{ rows: FormRow[] }>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/compound`,
      payload,
    );
    return res.data;
  },

  // ----- Additional document entries (AdditionalDocuments step) -----

  addAdditionalDocumentEntry: async (
    templateId: string,
    stepId: string,
    body: { label: string },
  ): Promise<AdditionalDocumentEntry> => {
    const res = await apiClient.post<AdditionalDocumentEntry>(
      `/workflows/${templateId}/steps/${stepId}/additional-document-entries`,
      body,
    );
    return res.data;
  },

  editAdditionalDocumentEntry: async (
    templateId: string,
    stepId: string,
    entryId: string,
    /** Whole-state: an omitted `labelFr` / `filenameTemplate` / `description` / `descriptionFr`
     *  is CLEARED on the server. */
    body: {
      label: string;
      labelFr?: string | null;
      allowedExtensions: string[];
      isRequired: boolean;
      doNotParseToPdf: boolean;
      maxFileSizeMb: number;
      filenameTemplate?: string | null;
      description?: string | null;
      descriptionFr?: string | null;
    },
  ): Promise<AdditionalDocumentEntry> => {
    const res = await apiClient.patch<AdditionalDocumentEntry>(
      `/workflows/${templateId}/steps/${stepId}/additional-document-entries/${entryId}`,
      body,
    );
    return res.data;
  },

  deleteAdditionalDocumentEntry: async (
    templateId: string,
    stepId: string,
    entryId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/workflows/${templateId}/steps/${stepId}/additional-document-entries/${entryId}`,
    );
  },

  /**
   * Replace the gating conditions on an additional document entry. Same wire
   * shape as setFileEntryConditions — empty list + null combinator clears it.
   */
  setAdditionalDocumentEntryConditions: async (
    templateId: string,
    stepId: string,
    entryId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<AdditionalDocumentEntry> => {
    const res = await apiClient.put<AdditionalDocumentEntry>(
      `/workflows/${templateId}/steps/${stepId}/additional-document-entries/${entryId}/conditions`,
      body,
    );
    return res.data;
  },

  /**
   * Replace an AdditionalDocuments step's document settings: the optional bucket's extension
   * allow-list and per-file cap, its visibility, the two COMBINED size caps, and the optional
   * document count.
   *
   * <p>
   * Every member but `extensions` is optional, and an omitted one leaves the persisted value
   * untouched — that is what lets each control in the drawer save on its own blur without
   * echoing the others back and clobbering a change made in between.
   * </p>
   *
   * <p>
   * An options OBJECT rather than positional arguments. There were already three trailing
   * optionals and callers were passing `undefined` positionally just to reach the one they
   * cared about; six would have been unreadable.
   * </p>
   */
  setOptionalDocumentExtensions: async (
    templateId: string,
    stepId: string,
    settings: {
      /** Omit to leave the persisted allow-list untouched; `[]` genuinely clears it. */
      extensions?: string[];
      maxFileSizeMb?: number;
      showSection?: boolean;
      mandatoryCombinedMaxSizeMb?: number;
      optionalCombinedMaxSizeMb?: number;
      maxOptionalDocuments?: number;
    },
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/optional-document-extensions`,
      settings,
    );
    return res.data;
  },

  // ----- OneSpanESign step config -----

  /**
   * Replace the OneSpan signing config on a OneSpanESign step. Pass `null` to
   * clear the config; pass a `OneSpanConfig` to overwrite it wholesale (drawer
   * authors everything in one form, so partial updates aren't supported).
   */
  setOneSpanConfig: async (
    templateId: string,
    stepId: string,
    config: OneSpanConfigPayload | null,
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/onespan-config`,
      config,
    );
    return res.data;
  },

  // ----- Welcome step config -----

  /**
   * Replace the welcome landing-card config on a Welcome step. Pass `null` to
   * clear back to runtime defaults; pass a full `WelcomeConfig` to overwrite it
   * wholesale (the modal authors everything in one form).
   */
  setWelcomeConfig: async (
    templateId: string,
    stepId: string,
    config: WelcomeConfig | null,
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/welcome-config`,
      config,
    );
    return res.data;
  },

  // ----- Thank You step config -----

  /**
   * Replace the confirmation-card config on a ThankYou step. Pass `null` to clear
   * back to the workflow-title default; pass a full `ThankYouConfig` to overwrite.
   */
  setThankYouConfig: async (
    templateId: string,
    stepId: string,
    config: ThankYouConfig | null,
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/thankyou-config`,
      config,
    );
    return res.data;
  },

  // ----- Editor lock -----

  /** Acquire (or refresh if already held by you) the editor lock. Resolves on 200/409. */
  acquireLock: async (templateId: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${templateId}/lock`);
    return res.data;
  },

  /** Release the editor lock. Idempotent — releasing a lock you don't hold is a silent no-op. */
  releaseLock: async (templateId: string): Promise<void> => {
    await apiClient.delete(`/workflows/${templateId}/lock`);
  },

  /**
   * Owner-only: forcibly clear the editor lock when a colleague has acquired it and
   * gone idle. Used as a "take back my workflow" affordance from the details page.
   * BE returns 403 for non-owners.
   */
  forceReleaseLock: async (templateId: string): Promise<void> => {
    await apiClient.delete(`/workflows/${templateId}/lock/force`);
  },

  /**
   * Release EVERY lock this user holds, in one call. Fired on logout and on session expiry,
   * before the session is torn down.
   *
   * Not expressible as a loop over `releaseLock`: at sign-off the client may hold locks from
   * several tabs and cannot reliably enumerate them, and it is about to lose the credentials
   * it would need for the follow-up calls. The server knows what the oid holds, so it does
   * the enumeration. Idempotent — 204 whether or not anything was held.
   */
  releaseAllMyLocks: async (): Promise<void> => {
    await apiClient.delete('/workflows/locks/held-by-me');
  },

  // ----- File transfer (streamed through the API — ADR-0028) -----
  //
  // These used to mint a presigned URL that the browser then PUT/GET straight to S3. BMO's
  // client firewall blocks connections to S3 hostnames, so the bytes now ride through the API
  // Lambda on the same allowlisted CloudFront origin as every other call. Practical ceiling is
  // ~4.4 MiB per file (API Gateway base64-encodes the body into a Lambda event capped at 6 MB);
  // the server enforces 4 MiB and echoes the number back as `maxUploadBytes`.

  /**
   * Uploads an originator PDF source template and returns the key to persist on the entry.
   */
  uploadFile: async (
    templateId: string,
    file: File,
  ): Promise<{ key: string; maxUploadBytes: number }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post<{ key: string; maxUploadBytes: number }>(
      `/workflows/${templateId}/files`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: FILE_TRANSFER_TIMEOUT_MS },
    );
    return res.data;
  },

  /**
   * Uploads an ADVISOR additional document (ADR-0019). Distinct from `uploadFile` above, which
   * writes originator PDF templates into a different bucket — this one writes customer PII and
   * is only ever called from advisor runtime, never from originator/approver form preview. See
   * `useAdditionalDocumentUpload`.
   *
   * NOTHING HERE NAMES THE DOCUMENT FOR BMO. The object is stored under the advisor's own
   * filename and the §14.1 delivery name is composed at the deliver call, from the description
   * they have settled on by then — so this request carries bytes, the application it belongs to,
   * and the entry id that decides whether it gets converted.
   *
   * A resolved promise does NOT mean the file is usable yet: it lands untagged and the bucket
   * policy denies reads until GuardDuty clears it. Callers poll `getAdditionalDocumentScanStatus`.
   */
  uploadAdditionalDocument: async (
    templateId: string,
    file: File,
    meta: {
      applicationId: string;
      /**
       * The originator entry this file fills; omitted for the unnamed optional bucket. The server
       * reads the entry's `doNotParseToPdf` flag from it to decide whether a conversion is owed
       * (ADR-0025 / ADR-0026), and its label becomes the DOCNAME at delivery time.
       */
      entryId?: string;
      /**
       * The workflow a form branch swapped in, when one fired. `templateId` stays the BASE form —
       * it is minted into the object's key, and that key is the ownership boundary the status
       * poll, the deliver call and submit's backstop all re-check against the base id.
       *
       * This is what the server resolves `entryId` against, because the branch target owns the
       * additional-documents step and every entry on it. Omitted on an unbranched form, which the
       * server reads the same way it reads a claim the base template does not declare: as the base
       * form's own entries.
       */
      subsequentTemplateId?: string | null;
    },
  ): Promise<{ key: string; storedFileName: string; maxUploadBytes: number }> => {
    const form = new FormData();
    form.append('file', file);
    form.append('applicationId', meta.applicationId);
    if (meta.entryId) form.append('entryId', meta.entryId);
    if (meta.subsequentTemplateId) form.append('subsequentTemplateId', meta.subsequentTemplateId);
    const res = await apiClient.post<{
      key: string;
      storedFileName: string;
      maxUploadBytes: number;
    }>(`/workflows/${templateId}/additional-documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: FILE_TRANSFER_TIMEOUT_MS,
    });
    return res.data;
  },

  /**
   * Batched readiness poll for advisor additional documents (ADR-0024, ADR-0025).
   *
   * An upload landing in S3 does NOT make the file usable. The object arrives untagged and the
   * bucket policy denies reads until GuardDuty clears it — and, for a file the originator's entry
   * requires as a PDF, a server-side conversion and a second malware scan on the conversion's own
   * output still follow. The advisor's browser polls this until every file comes back `ready`; see
   * `useAdditionalDocumentScanStatus` for the cadence and budget.
   *
   * All of that reports as one `processing` state — the UI says "Uploading…" throughout. See
   * `FileState` in `uploadedFiles.ts`.
   *
   * Batched deliberately: one call covers every pending file, so a step with eight attachments
   * costs the same four requests a minute as a step with one.
   */
  getAdditionalDocumentScanStatus: async (
    templateId: string,
    keys: string[],
  ): Promise<AdditionalDocumentScanStatus[]> => {
    const res = await apiClient.post<{ results: AdditionalDocumentScanStatus[] }>(
      // `file-status`, not `scan-status`: the route reports conversion as well as scanning.
      `/workflows/${templateId}/additional-documents/file-status`,
      { keys },
    );
    // Same CloudFront SPA-fallback trap as the two minters above (B17): a 404 from the API origin
    // comes back as 200 + index.html, so `results` reads `undefined`. Throwing here routes it to
    // the poller's transient-error path, which keeps polling — the right call, because a rewritten
    // response says nothing about whether the file is safe.
    if (!Array.isArray(res.data?.results)) {
      throw new Error('Additional-document scan-status request did not return results.');
    }
    return res.data.results;
  },

  /**
   * Fetches a stored workflow file as a Blob.
   *
   * MUST go through `apiClient` rather than a bare `fetch` or a `window.open`, for two reasons.
   * A 404 from the API arrives as `200` + index.html because CloudFront's SPA fallback is
   * distribution-wide (B17), and only apiClient's response interceptor catches that — a bare
   * fetch would hand the SPA shell to pdf.js as if it were a PDF. And apiClient is what attaches
   * the dev-login identity header, so a direct `window.open` 401s under dev login.
   */
  downloadFile: async (templateId: string, key: string): Promise<Blob> => {
    const res = await apiClient.get<Blob>(`/workflows/${templateId}/files`, {
      params: { key },
      responseType: 'blob',
      timeout: FILE_TRANSFER_TIMEOUT_MS,
    });
    return res.data;
  },

  /**
   * Generates ONE filled PDF server-side and returns its bytes (ADR-0021, ADR-0031).
   *
   * The advisor's answers go up; a filled, anchor-stamped PDF comes back. Neither
   * the blank template nor the fill logic reaches the browser any more, and the
   * server fills against the PUBLISHED template version rather than trusting the
   * client's copy of the field mappings.
   *
   * `language` is not just a file picker. It selects which of the entry's two
   * documents is filled, which signature placements are stamped, AND which
   * language-scoped `pdfMappings` apply — the RIF language checkbox shares one
   * AcroForm name and two export values across both templates with the boxes
   * swapped, so the wrong value here ticks the wrong box rather than missing.
   *
   * `values` is the RAW answer map, deliberately not `buildApplicationSnapshot`'s
   * `plctData`: the snapshot flattens to bare field ids and drops the
   * `inst:<sectionId>:<idx>:<fieldId>` keys, so a repeatable section would fill
   * every instance blank.
   *
   * Same `apiClient` requirement as {@link downloadFile} — a bare `fetch` would
   * hand CloudFront's SPA fallback (B17) to the viewer as if it were a PDF, and
   * would drop the dev-login identity header.
   */
  generatePdfPreview: async (
    templateId: string,
    body: {
      entryId: string;
      key: string;
      language: 'en' | 'fr';
      values: Record<string, unknown>;
    },
  ): Promise<Blob> => {
    const res = await apiClient.post<Blob>(`/workflows/${templateId}/pdf/preview`, body, {
      responseType: 'blob',
      timeout: FILE_TRANSFER_TIMEOUT_MS,
    });
    return res.data;
  },

  // ----- Approver -----

  queue: async (): Promise<WorkflowTemplate[]> => {
    const res = await apiClient.get<WorkflowTemplate[]>('/workflows/queue');
    return res.data;
  },

  // Orphaned as of the Phase 9 audit work — the Audit History page now uses auditLog().
  // The backend /workflows/recent-decisions endpoint still exists; restore this (and the
  // WorkflowDecision type + RecentDecisions component) if a recent-decisions view returns.
  // recentDecisions: async (): Promise<WorkflowDecision[]> => {
  //   const res = await apiClient.get<WorkflowDecision[]>('/workflows/recent-decisions');
  //   return res.data;
  // },

  /**
   * Audit History feed. Search and date range are applied SERVER-side: the feed
   * is capped to the newest N rows, so filtering in the browser would only ever
   * search inside that window — a date range covering last month would come back
   * empty whenever the newest N rows are all recent.
   *
   * @param q    matches submitter name, email, or oid (case-insensitive substring)
   * @param from inclusive lower bound, ISO-8601
   * @param to   inclusive upper bound, ISO-8601 (the BE widens a midnight value to
   *             end-of-day so a single-day range selects that whole day)
   */
  auditLog: async (params?: {
    q?: string;
    from?: string;
    to?: string;
  }): Promise<AuditLogEntry[]> => {
    const res = await apiClient.get<AuditLogEntry[]>('/workflows/audit', {
      params: {
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.from ? { from: params.from } : {}),
        ...(params?.to ? { to: params.to } : {}),
      },
    });
    return res.data;
  },

  approve: async (
    id: string,
    versionNumber: number,
    language?: string,
  ): Promise<WorkflowTemplate> => {
    // ?lang=<en|fr> drives the SES notification body's single-language render.
    // Omitted → the BE falls back to English. Kept optional so existing callers
    // (unit tests, non-i18n contexts) don't break.
    const qs = language ? `?lang=${encodeURIComponent(language)}` : '';
    const res = await apiClient.post<WorkflowTemplate>(
      `/workflows/${id}/versions/${versionNumber}/approve${qs}`,
    );
    return res.data;
  },

  reject: async (
    id: string,
    versionNumber: number,
    comment: string,
    language?: string,
  ): Promise<WorkflowTemplate> => {
    const qs = language ? `?lang=${encodeURIComponent(language)}` : '';
    const res = await apiClient.post<WorkflowTemplate>(
      `/workflows/${id}/versions/${versionNumber}/reject${qs}`,
      { comment },
    );
    return res.data;
  },
};
